import { Component, signal, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface PurseAssignmentSummary {
    playerCount: number;
    totalAssigned: number;
    totalProjected: number;
    variance: number;
    warningCount: number;
    assignments: Array<{
        playerName: string;
        assigned: number;
        projected: number;
        variancePercent: number;
    }>;
}

@Component({
    selector: 'app-purse-assignment-confirmation-dialog',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <button type="button" class="close" (click)="onCancel()">
                        <span aria-hidden="true">&times;</span>
                    </button>
                    <h4 class="modal-title">
                        <i class="fa fa-question-circle text-warning"></i>
                        Confirm Purse Assignments
                    </h4>
                </div>
                <div class="modal-body">
                    <div class="alert alert-info">
                        <i class="fa fa-info-circle"></i>
                        You are about to save assignments for <strong>{{ summary().playerCount }}</strong> player(s).
                    </div>

                    <div class="row m-b-md">
                        <div class="col-sm-6">
                            <div class="stat-box">
                                <h5 class="text-muted">Total Projected</h5>
                                <h3 class="text-navy">{{ formatCurrency(summary().totalProjected) }}</h3>
                            </div>
                        </div>
                        <div class="col-sm-6">
                            <div class="stat-box">
                                <h5 class="text-muted">Total Assigned</h5>
                                <h3 class="text-success">{{ formatCurrency(summary().totalAssigned) }}</h3>
                            </div>
                        </div>
                    </div>

                    @if (summary().variance !== 0) {
                        <div class="alert" [ngClass]="summary().variance > 0 ? 'alert-warning' : 'alert-success'">
                            <i class="fa fa-exclamation-triangle"></i>
                            <strong>Variance: {{ formatCurrency(summary().variance) }}</strong>
                            <span class="pull-right">
                                {{ summary().variance > 0 ? 'Over' : 'Under' }} projected by 
                                {{ ((Math.abs(summary().variance) / summary().totalProjected) * 100).toFixed(2) }}%
                            </span>
                        </div>
                    }

                    @if (summary().warningCount > 0) {
                        <div class="alert alert-warning">
                            <i class="fa fa-warning"></i>
                            <strong>{{ summary().warningCount }}</strong> assignment(s) exceed 10% variance threshold
                        </div>
                    }

                    <h5 class="m-t-md m-b-sm">Assignment Details:</h5>
                    <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                        <table class="table table-striped table-condensed">
                            <thead>
                                <tr>
                                    <th>Player</th>
                                    <th class="text-right">Projected</th>
                                    <th class="text-right">Assigned</th>
                                    <th class="text-right">Variance</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (assignment of summary().assignments; track assignment.playerName) {
                                    <tr [class.warning]="assignment.variancePercent > 10">
                                        <td>{{ assignment.playerName }}</td>
                                        <td class="text-right">{{ formatCurrency(assignment.projected) }}</td>
                                        <td class="text-right font-bold">{{ formatCurrency(assignment.assigned) }}</td>
                                        <td class="text-right">
                                            <span [ngClass]="getVarianceClass(assignment.variancePercent)">
                                                {{ assignment.variancePercent.toFixed(1) }}%
                                            </span>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-white" (click)="onCancel()">
                        <i class="fa fa-times"></i> Cancel
                    </button>
                    <button type="button" class="btn btn-primary" (click)="onConfirm()">
                        <i class="fa fa-check"></i> Confirm & Save
                    </button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .stat-box {
            text-align: center;
            padding: 15px;
            background: #f8f8f8;
            border-radius: 4px;
        }

        .stat-box h5 {
            margin: 0 0 10px 0;
            font-size: 12px;
            text-transform: uppercase;
        }

        .stat-box h3 {
            margin: 0;
            font-weight: 600;
        }

        .table-condensed > thead > tr > th,
        .table-condensed > tbody > tr > td {
            padding: 5px;
        }

        .font-bold {
            font-weight: 600;
        }

        .m-b-md {
            margin-bottom: 20px;
        }

        .m-t-md {
            margin-top: 20px;
        }

        .m-b-sm {
            margin-bottom: 10px;
        }
    `]
})
export class PurseAssignmentConfirmationDialogComponent {
    summary = input.required<PurseAssignmentSummary>();
    Math = Math;

    onConfirm(): void {
        // Emit confirm event (will be handled by parent)
        const modalElement = document.querySelector('.modal');
        if (modalElement) {
            (modalElement as any).confirmResult = true;
        }
    }

    onCancel(): void {
        // Emit cancel event (will be handled by parent)
        const modalElement = document.querySelector('.modal');
        if (modalElement) {
            (modalElement as any).confirmResult = false;
        }
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    }

    getVarianceClass(variancePercent: number): string {
        const absVariance = Math.abs(variancePercent);
        if (absVariance > 15) {
            return 'text-danger';
        } else if (absVariance > 10) {
            return 'text-warning';
        }
        return 'text-success';
    }
}
